"use client";
import { useActionState, useState } from "react";
import { usePathname } from "next/navigation";
import { Button, Container, FormStatus, HoneypotField, Input, Textarea } from "@cw/ui";
import { submitContact } from "@/actions";
import type { FormResult } from "@cw/forms";

export function ContactForm({ variant = "contact", submitLabel = "Send", messageLabel = "How can we help?", facts = [] }: { variant?: "contact" | "demo"; submitLabel?: string; messageLabel?: string; facts?: string[] }) {
  const [state, action, pending] = useActionState<FormResult | null, FormData>(submitContact, null);
  const [values, setValues] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const pathname = usePathname();
  const errors = state && !state.ok ? state.errors : {};
  if (state?.ok) {
    return (
      <section className="bg-paper pb-24"><Container className="max-w-2xl">
        <FormStatus state="success" success={variant === "demo" ? "Demo request received. An engineer will reach out within one business day to set a time." : "Message received. We'll reply within one business day."} />
      </Container></section>
    );
  }
  return (
    <section className="bg-paper pb-24">
      <Container>
        <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <form action={action} noValidate className="relative grid gap-5 rounded-card border border-hairline bg-paper-raised p-7 shadow-card md:p-9">
            <input type="hidden" name="variant" value={variant} />
            <input type="hidden" name="sourcePath" value={pathname ?? ""} />
            <HoneypotField />
            <div className="grid gap-5 sm:grid-cols-2">
              <Input id="name" label="Name" required autoComplete="name" error={errors.name} value={values.name} onChange={(event) => setValues((current) => ({ ...current, name: event.target.value }))} />
              <Input id="email" label="Work email" type="email" required autoComplete="email" error={errors.email} value={values.email} onChange={(event) => setValues((current) => ({ ...current, email: event.target.value }))} />
              <Input id="company" label="Company" autoComplete="organization" error={errors.company} value={values.company} onChange={(event) => setValues((current) => ({ ...current, company: event.target.value }))} />
              <Input id="phone" label="Phone" type="tel" autoComplete="tel" error={errors.phone} value={values.phone} onChange={(event) => setValues((current) => ({ ...current, phone: event.target.value }))} />
            </div>
            <Textarea id="message" label={messageLabel} rows={5} error={errors.message} value={values.message} onChange={(event) => setValues((current) => ({ ...current, message: event.target.value }))} />
            {errors._form ? <FormStatus state="error" success="" error={errors._form} /> : null}
            <div><Button type="submit" size="lg" disabled={pending}>{pending ? "Sending…" : submitLabel}</Button></div>
          </form>
          {facts.length ? (
            <aside className="lg:pt-2">
              <p className="font-sans text-xs font-semibold uppercase tracking-[0.18em] text-signal-ink">What to expect</p>
              <ul className="mt-4 space-y-3">
                {facts.map((f) => <li key={f} className="flex gap-3 text-[15px] text-slate-strong"><span aria-hidden="true" className="mt-[0.6em] h-1.5 w-1.5 shrink-0 rounded-full bg-signal" />{f}</li>)}
              </ul>
            </aside>
          ) : null}
        </div>
      </Container>
    </section>
  );
}
